<template>
  <div class="erp-finance-container">
    <!-- 头部工具栏 -->
    <div class="erp-header">
      <div class="header-left">
        <h2 class="erp-title">
          <i class="el-icon-s-finance"></i>
          现金银行期初
        </h2>
        <div class="erp-status">
          <el-tag type="success" size="small">
            共 {{ displayData.length }} 个科目
          </el-tag>
          <el-tag type="warning" size="small" style="margin-left: 8px;">
            合计: {{ formatMoney(totalAmount) }}
          </el-tag>
        </div>
      </div>
      <div class="header-right">
        <div class="erp-actions">
          <el-button
              type="primary"
              size="mini"
              :loading="loading"
              @click="initData"
          >刷新</el-button>
        </div>
      </div>
    </div>

    <div class="erp-main-content">
      <div class="erp-table-panel" style="width: 100%;">
        <div class="panel-header">
          <div class="panel-title">
            <i class="el-icon-s-grid"></i>
            <span>期初余额明细表</span>
          </div>
          <div class="panel-actions">
            <el-tooltip content="isBegin=0 可修改；修改后点击输入框右侧按钮保存；isBegin=1 只读" placement="top">
              <i class="el-icon-info" style="color: #909399; font-size: 16px;"></i>
            </el-tooltip>
          </div>
        </div>

        <div class="table-container" v-loading="loading">
          <el-table
              :data="displayData"
              border
              size="small"
              style="width: 100%"
              :header-cell-style="{ background: '#f8f9fa', color: '#606266' }"
              :row-class-name="tableRowClassName"
              class="erp-table"
              row-key="id"
          >
            <el-table-column label="序号" width="60" align="center" fixed>
              <template slot-scope="scope">
                <span class="row-index">{{ scope.$index + 1 }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="name" label="科目" min-width="300">
              <template slot-scope="{ row }">
                <div
                    class="name-cell"
                    :class="{ 'is-parent': row.hasChildren, 'is-child': row.level > 0 }"
                >
                  <template v-if="row.level > 0">
                    <span class="tree-line"></span>
                    <span class="tree-dot"></span>
                  </template>

                  <i :class="getNodeIcon(row)" :style="{ color: getNodeColor(row), marginRight: '8px' }"></i>
                  <span>{{ row.name }}</span>

                  <el-tag
                      v-if="!row.hasChildren && !row.editable"
                      type="info"
                      size="mini"
                      effect="plain"
                      style="margin-left: 8px; font-weight: 400;"
                  >已锁定</el-tag>
                </div>
              </template>
            </el-table-column>

            <el-table-column prop="amount" label="期初数（元）" min-width="380">
              <template slot-scope="{ row }">
                <div
                    class="amount-cell"
                    :style="{ paddingLeft: (row.level || 0) * 28 + 'px' }"
                >
                  <!-- 1. 父节点：汇总数字 -->
                  <div v-if="row.hasChildren" class="amount-sum">
                    <span class="sum-value">{{ formatMoney(row.amount) }}</span>
                  </div>

                  <!-- 2. 只读：直接展示 -->
                  <div v-else-if="!row.editable" class="amount-readonly">
                    <span class="readonly-value">{{ formatMoney(row.amount) }}</span>
                  </div>

                  <!-- 3. 可编辑：输入框（append 内嵌保存按钮） -->
                  <div v-else class="amount-edit-wrap">
                    <el-input
                        v-model="row.amount"
                        type="number"
                        size="small"
                        placeholder="请输入期初数"
                        class="amount-input"
                        @keyup.enter.native="saveRow(row)"
                    >
                      <!-- 保存按钮放入输入框 append 区域 -->
                      <template slot="append">
                        <el-tooltip
                            :content="row._saveStatus === 'saving'
                                ? '保存中...'
                                : (isRowDirty(row) ? '点击保存' : '无修改')"
                            placement="top"
                            :open-delay="300"
                        >
                          <span
                              class="save-btn-inline"
                              :class="{
                                'is-dirty': isRowDirty(row),
                                'is-saving': row._saveStatus === 'saving',
                                'is-error': row._saveStatus === 'error'
                              }"
                              @click.stop="saveRow(row)"
                          >
                            <i
                                v-if="row._saveStatus === 'saving'"
                                class="el-icon-loading"
                            ></i>
                            <i
                                v-else-if="row._saveStatus === 'error'"
                                class="el-icon-warning-outline"
                            ></i>
                            <i
                                v-else
                                class="el-icon-finished"
                            ></i>
                          </span>
                        </el-tooltip>
                      </template>
                    </el-input>

                    <span
                        v-if="row._saveStatus === 'saved'"
                        class="save-status saved">
                      <i class="el-icon-circle-check"></i> 已保存
                    </span>
                    <span
                        v-else-if="row._saveStatus === 'error'"
                        class="save-status error"
                        @click="saveRow(row)">
                      <i class="el-icon-warning-outline"></i> 保存失败，点击重试
                    </span>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllFinanceclass, getFundList, addFundInitial } from "@/api/clasificacionContable/clasificacionContable";

export default {
  name: 'OpeningBalanceEntry',
  data() {
    return {
      loading: false,
      tableData: [],
      displayData: [],
    }
  },
  computed: {
    totalAmount() {
      let sum = 0
      this.tableData.forEach(item => {
        if (item.hasChildren) {
          sum += Number(item.amount) || 0
        } else if (item.amount != null) {
          sum += Number(item.amount) || 0
        }
      })
      return sum
    }
  },

  created() {
    this.initData()
  },

  methods: {
    async initData() {
      await this.loadData()
    },

    async loadData() {
      this.loading = true
      try {
        const [classRes, fundRes] = await Promise.all([
          getAllFinanceclass(),
          getFundList()
        ])

        const tree = classRes?.children || classRes?.data?.children || []
        const idMap = this.buildIdMap(tree)

        const fundMap = {}
        const fundList = fundRes?.data || []
        fundList.forEach(item => {
          fundMap[String(item.financeId)] = {
            amount: Number(item.amount) || 0,
            isBegin: item.isBegin
          }
        })

        this.buildTableData(idMap, fundMap)
        this.updateDisplayData()
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败：' + (error.message || '网络错误'))
      } finally {
        this.loading = false
      }
    },

    tableRowClassName({ row }) {
      if (row.hasChildren) return 'row-parent'
      if (row.level > 0) return 'row-child'
      return ''
    },

    buildIdMap(tree) {
      const map = {}
      const walk = (nodes, parentId = null) => {
        if (!nodes || !nodes.length) return
        nodes.forEach(n => {
          const id = String(n.id)
          map[id] = {
            id,
            name: n.label || n.text || n.name || '',
            parentId: parentId ? String(parentId) : null,
            children: n.children || []
          }
          if (n.children?.length) walk(n.children, id)
        })
      }
      walk(tree)
      return map
    },

    isFundEditable(fundInfo) {
      if (!fundInfo) return true
      const v = fundInfo.isBegin
      if (v === 1 || v === '1' || v === true || v === 'true') return false
      return true
    },

    isDescendantOf(idMap, nodeId, ancestorId) {
      let cur = String(nodeId)
      let guard = 0
      while (cur && guard < 100) {
        if (cur === String(ancestorId)) return true
        cur = idMap[cur]?.parentId
        guard++
      }
      return false
    },

    buildTableData(idMap, fundMap) {
      this.tableData = []

      const CASH_ID = '101'
      const BANK_ID = '102'

      const cashInfo = idMap[CASH_ID]
      const cashFund = fundMap[CASH_ID]
      this.tableData.push({
        id: 'cash',
        financeId: CASH_ID,
        name: cashInfo?.name || '现金',
        amount: cashFund?.amount || 0,
        level: 0,
        parentId: null,
        editable: this.isFundEditable(cashFund),
        hasChildren: false
      })

      const bankChildren = []
      Object.keys(fundMap).forEach(fid => {
        if (fid === BANK_ID) return
        if (!idMap[fid]) return
        if (this.isDescendantOf(idMap, fid, BANK_ID)) {
          const f = fundMap[fid]
          bankChildren.push({
            id: `bank_${fid}`,
            financeId: fid,
            name: idMap[fid].name || `银行${fid}`,
            amount: f?.amount || 0,
            level: 1,
            parentId: 'bank_parent',
            editable: this.isFundEditable(f),
            hasChildren: false
          })
        }
      })
      this.tableData.push({
        id: 'bank_parent',
        financeId: BANK_ID,
        name: idMap[BANK_ID]?.name || '银行账号',
        amount: bankChildren.reduce((s, c) => s + (Number(c.amount) || 0), 0),
        level: 0,
        parentId: null,
        editable: false,
        hasChildren: true,
        children: bankChildren
      })

      const gatherChildren = []
      Object.keys(fundMap).forEach(fid => {
        if (fid === CASH_ID || fid === BANK_ID) return
        if (!idMap[fid]) return
        if (this.isDescendantOf(idMap, fid, BANK_ID)) return
        if (this.isDescendantOf(idMap, fid, CASH_ID)) return
        const f = fundMap[fid]
        gatherChildren.push({
          id: `gather_${fid}`,
          financeId: fid,
          name: idMap[fid].name || `聚合支付${fid}`,
          amount: f?.amount || 0,
          level: 1,
          parentId: 'gather_parent',
          editable: this.isFundEditable(f),
          hasChildren: false
        })
      })
      this.tableData.push({
        id: 'gather_parent',
        financeId: null,
        name: '聚合支付',
        amount: gatherChildren.reduce((s, c) => s + (Number(c.amount) || 0), 0),
        level: 0,
        parentId: null,
        editable: false,
        hasChildren: true,
        children: gatherChildren
      })

      // 关键：用 $set 保证 _saveStatus / _originalAmount 响应式
      const initMeta = (nodes) => {
        nodes.forEach(n => {
          this.$set(n, '_originalAmount', Number(n.amount) || 0)
          this.$set(n, '_saveStatus', '')
          if (n.children?.length) initMeta(n.children)
        })
      }
      initMeta(this.tableData)
    },

    updateDisplayData() {
      const displayList = []
      const addNode = (node) => {
        displayList.push(node)
        if (node.children && node.children.length) {
          node.children.forEach(child => addNode(child))
        }
      }
      this.tableData.forEach(node => addNode(node))
      this.displayData = displayList
    },

    updateParentAmount(childRow) {
      if (!childRow.parentId) return
      const parent = this.tableData.find(n => n.id === childRow.parentId)
      if (parent && parent.children) {
        parent.amount = parent.children.reduce(
            (sum, c) => sum + (parseFloat(c.amount) || 0), 0
        )
      }
    },

    isRowDirty(row) {
      if (!row || !row.editable || row.hasChildren) return false
      const v = parseFloat(row.amount)
      const cur = isNaN(v) ? 0 : v
      return Number(cur) !== Number(row._originalAmount)
    },

    async saveRow(row) {
      if (!row.editable || row.hasChildren || !row.financeId) return
      if (row._saveStatus === 'saving') return

      let value = parseFloat(row.amount)
      if (isNaN(value)) value = 0
      row.amount = value
      this.updateParentAmount(row)

      // 脏检查
      if (Number(value) === Number(row._originalAmount)) {
        this.$message.info('金额未修改')
        return
      }

      row._saveStatus = 'saving'
      try {
        const res = await addFundInitial({
          financeId: String(row.financeId),
          amount: value
        })

        // 兼容 code 数字/字符串 200 以及 success 字段
        const isOk = res && (res.code == 200 || res.success === true)

        if (isOk) {
          row._originalAmount = value
          row._saveStatus = 'saved'
          row.editable = false
          this.clearSavedStatus(row)
        } else {
          // 业务失败（code != 200）：立即恢复按钮
          row._saveStatus = 'error'
          this.$message.error((res && res.message) || '保存失败')
        }
      } catch (error) {
        row._saveStatus = 'error'
        this.$message.error('保存失败：' + (error.message || '网络错误'))
      } finally {
        // 兜底，绝不卡在 saving
        if (row._saveStatus === 'saving') {
          row._saveStatus = 'error'
        }
      }
    },

    clearSavedStatus(row) {
      setTimeout(() => {
        if (row._saveStatus === 'saved') row._saveStatus = ''
      }, 1500)
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '0.00'
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2)
    },

    getNodeIcon(data) {
      if (data.hasChildren) return 'el-icon-folder-opened'
      if (data.name === '现金') return 'el-icon-money'
      if (data.parentId === 'bank_parent') return 'el-icon-bank-card'
      if (data.parentId === 'gather_parent') return 'el-icon-connection'
      return 'el-icon-document'
    },

    getNodeColor(data) {
      if (data.hasChildren) return '#409EFF'
      if (data.name === '现金') return '#67C23A'
      if (data.parentId === 'bank_parent') return '#409EFF'
      if (data.parentId === 'gather_parent') return '#9B59B6'
      return '#909399'
    }
  }
}
</script>

<style lang="less" scoped>
.erp-finance-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  padding: 20px;

  .erp-header {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-left {
      .erp-title {
        margin: 0;
        font-size: 20px;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 24px;
          color: #409EFF;
        }
      }

      .erp-status {
        margin-top: 8px;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;
    }
  }

  .erp-main-content {
    flex: 1;
    display: flex;
    gap: 20px;
    overflow: hidden;

    .erp-table-panel {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      min-width: 0;

      .panel-header {
        padding: 16px 20px;
        border-bottom: 1px solid #ebeef5;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .panel-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #303133;

          i {
            color: #409EFF;
          }
        }

        .panel-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      }

      .table-container {
        flex: 1;
        padding: 20px;
        overflow-y: auto;

        .erp-table {
          /deep/ .el-table__header-wrapper th {
            font-weight: 600;
          }

          /deep/ .el-table__row.row-parent {
            background-color: #f7f9fc;

            td {
              background-color: #f7f9fc !important;
              border-bottom-color: #e4e7ed;
            }

            &:hover td {
              background-color: #eef2f8 !important;
            }
          }

          /deep/ .el-table__body-wrapper tr:hover td {
            background-color: #fafbfc;
          }

          .row-index {
            color: #909399;
            font-weight: 500;
          }

          /* ===== 科目列 ===== */
          .name-cell {
            display: flex;
            align-items: center;
            padding-left: 4px;

            &.is-parent {
              font-weight: 600;
              color: #303133;
              padding-left: 4px;
            }

            &.is-child {
              position: relative;
              padding-left: 28px;

              .tree-line {
                position: absolute;
                left: 10px;
                top: -50%;
                height: 100%;
                width: 1px;
                background: #dcdfe6;
              }

              .tree-dot {
                position: absolute;
                left: 6px;
                top: 50%;
                transform: translateY(-50%);
                width: 9px;
                height: 9px;
                border-radius: 50%;
                background: #fff;
                border: 2px solid #409EFF;
                box-sizing: border-box;
              }
            }
          }

          /* ===== 期初数列 ===== */
          .amount-cell {
            display: flex;
            align-items: center;
            min-height: 34px;
          }

          .amount-sum {
            .sum-value {
              font-weight: 600;
              color: #E6A23C;
              font-size: 14px;
            }
          }

          .amount-readonly {
            .readonly-value {
              font-weight: 500;
              color: #909399;
              font-size: 14px;
            }
          }

          .amount-edit-wrap {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;

            .amount-input {
              flex: 1;
              max-width: 300px;

              /deep/ input {
                text-align: right;
              }

              /* 内嵌保存按钮的 append 区域 */
              /deep/ .el-input-group__append {
                padding: 0;
                border-left: none;
                background-color: #f5f7fa;
                width: 34px;
                text-align: center;
              }
            }

            /* 内嵌保存按钮 */
            .save-btn-inline {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 32px;
              height: 28px;
              cursor: pointer;
              color: #c0c4cc;
              font-size: 16px;
              transition: all 0.2s ease;
              user-select: none;

              i {
                font-weight: bold;
              }

              /* 有修改：绿色高亮 */
              &.is-dirty {
                color: #67C23A;
                background-color: #f0f9eb;
              }

              &.is-dirty:hover {
                color: #fff;
                background-color: #67C23A;
              }

              /* 保存中：橙色 */
              &.is-saving {
                color: #E6A23C;
                cursor: wait;
                background-color: #fdf6ec;
              }

              /* 失败：红色 */
              &.is-error {
                color: #F56C6C;
                background-color: #fef0f0;
              }

              &.is-error:hover {
                color: #fff;
                background-color: #F56C6C;
              }
            }

            .save-status {
              font-size: 12px;
              white-space: nowrap;
              user-select: none;
              display: inline-flex;
              align-items: center;
              gap: 2px;

              &.saved {
                color: #67C23A;
                animation: fadeInOut 2s ease;
              }

              &.error {
                color: #F56C6C;
                cursor: pointer;

                &:hover {
                  text-decoration: underline;
                }
              }
            }
          }
        }
      }
    }
  }
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateX(-4px); }
  20% { opacity: 1; transform: translateX(0); }
  80% { opacity: 1; }
  100% { opacity: 0; }
}

.table-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>